import type { AnalyticsCustomerOrder } from '@/lib/analytics'
import type { CdekTrackingData } from '@/lib/api/schemas/cdek.schema'
import { COURIER_NOT_RECEIVED_TEXT } from '@/lib/courier-assignment'
import {
  formatCdekStatusName,
  type CustomerCourier,
  type CustomerCouriers,
  type CustomerShipmentState,
} from '@/lib/customer-state'
import { latestCdekStatus } from '@/lib/cdek-status-history'
import { STATUS_LABELS } from '@/lib/order-helpers'
import { DeliveryStateBadge } from './DeliveryStateBadge'

function money(value?: number): string {
  return typeof value === 'number' ? `${Math.round(value).toLocaleString('ru-RU')} ₽` : '—'
}

function date(value?: string, withTime = false): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return new Intl.DateTimeFormat('ru-RU', withTime
    ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: 'short', year: 'numeric' }
  ).format(parsed).replace('.', '')
}

function records(value: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
  }
  return value && typeof value === 'object' ? [value as Record<string, unknown>] : []
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined
}

function location(data?: CdekTrackingData): string {
  const from = [data?.from_location?.city, data?.from_location?.address].filter(Boolean).join(', ')
  const to = [data?.to_location?.city, data?.to_location?.address].filter(Boolean).join(', ')
  return [from, to].filter(Boolean).join(' → ') || 'Маршрут уточняется'
}

function CourierDetails({ courier }: { courier: CustomerCourier }) {
  const vehicle = [courier.car_model, courier.registration_mark].filter(Boolean).join(' · ')
  return (
    <div className="min-w-0">
      <div className={`truncate text-[11px] font-medium ${courier.name ? 'text-ink' : 'text-ink-faint'}`}>
        {courier.name ?? 'Имя не передано'}
      </div>
      {courier.phone
        ? <a href={`tel:${courier.phone}`} className="block mt-0.5 truncate text-[12px] font-semibold text-gold-deep tab-nums">{courier.phone}</a>
        : <div className="mt-0.5 text-[10px] text-ink-faint">Телефон не передан</div>}
      <div className="mt-0.5 truncate text-[9px] text-ink-faint">{vehicle || 'Автомобиль не указан'}</div>
      <div className="mt-0.5 text-[9px] text-ink-faint tab-nums">назначен {date(courier.assigned_at, true)}</div>
    </div>
  )
}

function CourierHistory({ couriers }: { couriers: CustomerCouriers }) {
  if (couriers.state === 'not_received') {
    return <div className="mt-2 text-[11px] text-ink-faint">{COURIER_NOT_RECEIVED_TEXT}</div>
  }
  return (
    <div className="mt-2">
      <CourierDetails courier={couriers.current} />
      {couriers.past.length > 0 && (
        <div className="mt-2.5 border-t border-rule/70 pt-2">
          <div className="text-[8px] font-semibold uppercase tracking-[0.08em] text-ink-faint">Прежние курьеры</div>
          <ul className="mt-1.5 space-y-2">
            {couriers.past.map((courier, index) => (
              <li key={`${courier.assigned_at}:${index}`}><CourierDetails courier={courier} /></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export function CustomerDeliveryCard({
  order,
  shipment,
  cdek,
  current,
}: {
  order?: AnalyticsCustomerOrder
  shipment?: CustomerShipmentState
  cdek?: CdekTrackingData
  current: boolean
}) {
  const latestStatus = latestCdekStatus(cdek?.statuses)
  const couriers = shipment?.couriers
  const reachableCourier = couriers?.state === 'assigned' && Boolean(couriers.current.phone)
  const problems = records(cdek?.delivery_problem)
  const delayReasons = records(cdek?.delay_reasons)
  const calls = records(cdek?.calls)[0]
  const failedCalls = records(calls?.failed_calls)
  const rescheduledCalls = records(calls?.rescheduled_calls)
  const issues = [...problems, ...delayReasons]
  const recipientPhones = cdek?.recipient?.phones?.map((entry) => entry.number).filter(Boolean) || []
  const packages = cdek?.packages || []
  const packageWeight = packages.reduce((sum, entry) => sum + (entry.weight || 0), 0)
  const plannedDate = shipment?.agreement?.date || shipment?.plannedDeliveryDate || cdek?.planned_delivery_date
  const agreementTime = shipment?.agreement
    ? [shipment.agreement.timeFrom, shipment.agreement.timeTo].filter(Boolean).join('–')
    : ''

  return (
    <article className={`border ${current ? 'border-gold/45 bg-gold/[0.035]' : 'border-rule bg-paper'}`}>
      <header className="px-3 sm:px-5 py-3 sm:py-4 border-b border-rule/80 flex items-start justify-between gap-3 sm:gap-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {current && <span className="px-1.5 sm:px-2 py-0.5 bg-onyx text-ivory text-[7px] sm:text-[8px] font-semibold uppercase tracking-[0.1em] rounded-full">Текущий заказ</span>}
            <span className="truncate text-[13px] sm:text-[15px] font-semibold tab-nums">Заказ №{order?.orderId || shipment?.orderId || '—'}</span>
            {shipment && <DeliveryStateBadge state={shipment.state} />}
          </div>
          <div className="mt-1 text-[8px] sm:mt-1.5 sm:text-[10px] text-ink-faint truncate">
            {shipment?.cdekNumber ? `СДЭК ${shipment.cdekNumber}` : 'Накладная СДЭК ещё не найдена'}
            {order?.date ? ` · создан ${date(order.date)}` : ''}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[15px] sm:text-[17px] font-semibold tab-nums">{money(order?.amount)}</div>
          <div className="mt-0.5 sm:mt-1 max-w-[110px] truncate text-[8px] sm:text-[9px] text-ink-faint">{STATUS_LABELS[order?.status || ''] || order?.status || 'Статус уточняется'}</div>
        </div>
      </header>

      <div className="p-3 sm:p-5">
        <div className="md:hidden space-y-2.5">
          <div>
            <div className="eyebrow">Состав</div>
            <div className="mt-1 text-[10px] leading-snug text-ink line-clamp-2">
              {order?.items.map((item) => `${item.name}${item.count > 1 ? ` ×${item.count}` : ''}`).join(', ') || 'Состав уточняется'}
            </div>
          </div>

          <div className="border-t border-rule/70 pt-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-3">
            <div className="min-w-0">
              <div className="eyebrow">Маршрут</div>
              <div className="mt-1 truncate text-[10px] text-ink">{location(cdek)}</div>
            </div>
            <div className="text-right">
              <div className="eyebrow">Доставка</div>
              <div className="mt-1 text-[10px] text-ink tab-nums">{plannedDate ? date(plannedDate) : 'Уточняется'}</div>
              {agreementTime && <div className="text-[8px] text-ink-faint tab-nums">{agreementTime}</div>}
            </div>
          </div>

          {(cdek?.recipient?.name || recipientPhones.length > 0) && (
            <div className="border-t border-rule/70 pt-2.5 min-w-0">
              <div className="eyebrow">Получатель</div>
              <div className="mt-1 truncate text-[10px] font-medium text-ink">{cdek?.recipient?.name || 'Имя уточняется'}</div>
              {recipientPhones[0] && <a href={`tel:${recipientPhones[0]}`} className="block mt-0.5 truncate text-[10px] text-gold-deep">{recipientPhones[0]}</a>}
            </div>
          )}
          {couriers ? (
            <div className="border-t border-rule/70 pt-2.5 min-w-0">
              <div className="eyebrow">Курьер</div>
              <CourierHistory couriers={couriers} />
            </div>
          ) : <div className="text-[8px] text-ink-faint">Курьер ещё не назначен</div>}
        </div>

        <div className="hidden md:grid md:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="bg-ivory/65 border border-rule/70 p-3">
            <div className="eyebrow">Состав</div>
            <div className="mt-2 text-[11px] leading-relaxed text-ink">
              {order?.items.map((item) => `${item.name}${item.count > 1 ? ` ×${item.count}` : ''}`).join(', ') || 'Состав уточняется'}
            </div>
            {packages.length > 0 && <div className="mt-2 text-[9px] text-ink-faint">{packages.length} мест{packageWeight ? ` · ${packageWeight.toLocaleString('ru-RU')} г` : ''}</div>}
          </div>

          <div className="bg-ivory/65 border border-rule/70 p-3">
            <div className="eyebrow">Маршрут и срок</div>
            <div className="mt-2 text-[11px] leading-relaxed text-ink">{location(cdek)}</div>
            <div className="mt-2 text-[9px] text-ink-faint">
              {plannedDate ? `План: ${date(plannedDate)}${agreementTime ? ` · ${agreementTime}` : ''}` : 'Дата доставки уточняется'}
              {cdek?.delivery_date ? ` · факт: ${date(cdek.delivery_date)}` : ''}
            </div>
          </div>

          <div className="bg-ivory/65 border border-rule/70 p-3">
            <div className="eyebrow">Получатель</div>
            <div className="mt-2 text-[11px] font-medium text-ink">{cdek?.recipient?.name || 'Имя уточняется'}</div>
            <div className="mt-1 text-[10px] text-ink-muted">
              {recipientPhones.length > 0
                ? recipientPhones.map((phone) => <a key={phone} href={`tel:${phone}`} className="block hover:text-gold-deep">{phone}</a>)
                : 'Телефон не найден'}
            </div>
          </div>

          <div className={`border p-3 ${reachableCourier ? 'bg-st-delivered/5 border-st-delivered/20' : 'bg-ivory/65 border-rule/70'}`}>
            <div className="eyebrow">Курьер</div>
            {couriers
              ? <CourierHistory couriers={couriers} />
              : <div className="mt-2 text-[11px] text-ink-faint">Ещё не назначен</div>}
          </div>
        </div>

        {(latestStatus || shipment?.statusCode) && (
          <div className="mt-2.5 sm:mt-3 border border-rule bg-bone/35 px-3 sm:px-4 py-2.5 sm:py-3 flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
            <div>
              <div className="eyebrow">Последнее движение</div>
              <div className="mt-1 text-[10px] sm:text-[12px] font-medium text-ink">
                {shipment?.statusName
                  || formatCdekStatusName(latestStatus?.code, latestStatus?.city)}
              </div>
              {(latestStatus?.city || latestStatus?.date_time) && <div className="mt-1 text-[9px] text-ink-faint">{[latestStatus.city, date(latestStatus.date_time, true)].filter(Boolean).join(' · ')}</div>}
            </div>
            {shipment?.problemCode && <span className="text-[10px] text-st-unpaid">Проблема {shipment.problemCode}</span>}
          </div>
        )}

        {(issues.length > 0 || failedCalls.length > 0 || rescheduledCalls.length > 0) && (
          <div className="mt-2.5 sm:mt-3 border border-st-unpaid/25 bg-st-unpaid/5 p-3 sm:p-4">
            <div className="eyebrow !text-st-unpaid">Проблемы и изменения доставки</div>
            <div className="mt-2 space-y-1.5 text-[10px] text-ink-muted">
              {issues.map((issue, index) => (
                <div key={`issue:${index}`}>Проблема {text(issue.code) || '—'}{text(issue.description || issue.name) ? ` · ${text(issue.description || issue.name)}` : ''}{text(issue.create_date) ? ` · ${date(text(issue.create_date), true)}` : ''}</div>
              ))}
              {failedCalls.map((call, index) => <div key={`failed:${index}`}>Неудачный звонок{call.date_time ? ` · ${date(text(call.date_time), true)}` : ''}{call.reason_code ? ` · причина ${text(call.reason_code)}` : ''}</div>)}
              {rescheduledCalls.map((call, index) => <div key={`rescheduled:${index}`}>Доставка перенесена{call.date_next ? ` на ${date(text(call.date_next))}` : ''}{call.time_next ? ` · ${text(call.time_next)}` : ''}{call.comment ? ` · ${text(call.comment)}` : ''}</div>)}
            </div>
          </div>
        )}

        {cdek?.statuses && cdek.statuses.length > 0 && (
          <details className="mt-3 border-t border-rule/70 pt-3 group">
            <summary className="cursor-pointer list-none flex items-center justify-between text-[10px] font-medium text-ink-muted hover:text-ink">
              Вся история движения · {cdek.statuses.length} событий
              <span className="group-open:rotate-90 transition-transform">›</span>
            </summary>
            <div className="mt-3 space-y-2 max-h-[260px] overflow-y-auto">
              {cdek.statuses.map((status, index) => (
                <div key={`${status.code}:${status.date_time || index}`} className="grid grid-cols-1 sm:grid-cols-[120px_1fr_130px] gap-1 sm:gap-3 text-[9px]">
                  <span className="text-ink-faint tab-nums">{date(status.date_time, true)}</span>
                  <span className="text-ink">{status.name || status.code}</span>
                  <span className="sm:text-right text-ink-faint">{status.city || ''}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </article>
  )
}
